// caiet UC91-05/06/10..15 — the filter bar. What it shows follows the role
// (UC91-05) and the chosen aggregation level: lot and site only mean something
// when nothing is aggregated (UC91-13/14), and the lot is picked from the lots the
// role reaches (LotFilterField). The date range defaults to
// [-30, +29] days (UC91-06). There is no granularity control: the interval
// decides the bucket (UC91-07).
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { MudChip, MudSelect } from '@egov-moldova/mud-react';
import {
  dateInputToIso,
  FilterBar,
  isoToDateInput,
  type ActiveFilter,
  DraftDateInput,
} from '@simsm/ui-shared';
import { LotFilterField } from './LotFilterField';
import {
  PERIOD_PRESETS,
  isValidPeriod,
  presetForRange,
  rangeForPreset,
  type AggregationLevel,
  type PeriodPresetId,
} from './stock-filters';
import type { ScopeOption, ScopeOptionLists } from './useStockScope';

export interface FilterState {
  from: string;
  to: string;
  level: AggregationLevel;
  regionIds: string[];
  localityIds: string[];
  registrarIdnos: string[];
  siteIds: string[];
  lots: string[];
}

interface FilterPanelProps {
  state: FilterState;
  levels: AggregationLevel[];
  // UC91-11 and UC91-12 are separate entitlements with separate sources, so they
  // are separate flags: an Observer reads the IMSP register but no geography.
  canFilterRegion: boolean;
  canFilterRegistrar: boolean;
  canFilterLocality: boolean;
  options: ScopeOptionLists;
  onChange: (patch: Partial<FilterState>) => void;
  onReset: () => void;
}

interface SelectChangeDetail {
  value: string;
}

interface MultiScopeFieldProps {
  label: string;
  placeholder: string;
  options: ScopeOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}

// AGE has no multi-select atom, so one is composed from the select listbox plus
// AGE's removable input chips, which is the composition
// wireframes/00-decisions.md D4 calls for. Picked values leave the listbox, so
// a value cannot be added twice.
function MultiScopeField({
  label,
  placeholder,
  options,
  selected,
  onChange,
}: MultiScopeFieldProps): JSX.Element | null {
  if (options.length === 0) return null;
  const labelOf = (value: string): string =>
    options.find((option) => option.value === value)?.label ?? value;
  const remaining = options.filter((option) => !selected.includes(option.value));
  const [first] = selected;

  return (
    <div className="simsm-filter-bar__field">
      <MudSelect
        size="medium"
        label={label}
        value=""
        placeholder={first === undefined ? placeholder : labelOf(first)}
        disabled={remaining.length === 0}
        onMudChange={(e: CustomEvent<SelectChangeDetail>) => {
          if (e.detail.value) onChange([...selected, e.detail.value]);
        }}
      >
        {remaining.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </MudSelect>
      {/* MUD adds the verb to the chip's ×: it reads "Elimină <value>" once, in the page language. */}
      {selected.length > 0 ? (
        <ul className="simsm-medmulti__chips">
          {selected.map((value) => (
            <li key={value}>
              <MudChip
                type="input"
                size="sm"
                removable
                onMudRemove={() => onChange(selected.filter((item) => item !== value))}
              >
                {labelOf(value)}
              </MudChip>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function FilterPanel({
  state,
  levels,
  canFilterRegion,
  canFilterRegistrar,
  canFilterLocality,
  options,
  onChange,
  onReset,
}: FilterPanelProps): JSX.Element {
  const { t } = useTranslate();
  const unaggregated = state.level === 'none';
  const derivedPeriod = presetForRange(state.from, state.to);
  // Asking for a custom interval is a view choice, not a filter: the range in
  // the address stays exactly what it was until the reader edits a date, so the
  // request does not change under them the moment they open the two fields.
  const [customAsked, setCustomAsked] = useState(false);
  const period: PeriodPresetId =
    derivedPeriod === 'custom' || customAsked ? 'custom' : derivedPeriod;
  // UC91-06 (TC-UC91-06-03): the wireframe marks both dates and says once, under
  // them, what a period needs. A preset is always valid, so this only ever shows on
  // the custom interval.
  const periodOk = isValidPeriod(state.from, state.to);

  const [defaultLevel] = levels;

  const labelIn = (list: ScopeOption[], value: string): string =>
    list.find((option) => option.value === value)?.label ?? value;

  // Every criterion the reader has moved off its default, as one removable chip
  // each — the many-valued scopes one chip per value, the way the wireframe
  // draws them (Stocuri.tsx:164-176).
  const activeFilters: ActiveFilter[] = [
    ...(period !== 'default'
      ? [
          {
            key: 'period',
            label: t('stockChart.filters.period'),
            value:
              period !== 'custom'
                ? t(`stockChart.period.${period}`)
                : periodOk
                  ? t('common.filter.range_value', { from: state.from, to: state.to })
                  : t('stockChart.period.incomplete'),
            onRemove: () => {
              setCustomAsked(false);
              const range = rangeForPreset('default');
              if (range) onChange(range);
            },
          },
        ]
      : []),
    ...(defaultLevel !== undefined && levels.length > 1 && state.level !== defaultLevel
      ? [
          {
            key: 'level',
            label: t('stockChart.filters.level'),
            value: t(`stockChart.level.${state.level}`),
            onRemove: () =>
              onChange(
                defaultLevel === 'none'
                  ? { level: defaultLevel }
                  : { level: defaultLevel, lots: [] },
              ),
          },
        ]
      : []),
    ...state.regionIds.map((value) => ({
      key: `region-${value}`,
      label: t('stockChart.filters.region'),
      value: labelIn(options.regions, value),
      onRemove: () =>
        onChange({
          regionIds: state.regionIds.filter((item) => item !== value),
          localityIds: [],
          siteIds: [],
        }),
    })),
    ...state.localityIds.map((value) => ({
      key: `locality-${value}`,
      label: t('stockChart.filters.locality'),
      value: labelIn(options.localities, value),
      onRemove: () =>
        onChange({
          localityIds: state.localityIds.filter((item) => item !== value),
          siteIds: [],
        }),
    })),
    ...state.registrarIdnos.map((value) => ({
      key: `registrar-${value}`,
      label: t('stockChart.filters.registrar'),
      value: labelIn(options.registrars, value),
      onRemove: () =>
        onChange({
          registrarIdnos: state.registrarIdnos.filter((item) => item !== value),
          siteIds: [],
        }),
    })),
    ...state.siteIds.map((value) => ({
      key: `site-${value}`,
      label: t('stockChart.filters.site'),
      value: labelIn(options.sites, value),
      onRemove: () => onChange({ siteIds: state.siteIds.filter((item) => item !== value) }),
    })),
    ...state.lots.map((value) => ({
      key: `lot-${value}`,
      label: t('stockChart.filters.lot'),
      value,
      onRemove: () => onChange({ lots: state.lots.filter((item) => item !== value) }),
    })),
  ];

  return (
    <FilterBar
      active={activeFilters}
      onReset={onReset}
      panelWidth={380}
      labels={{
        filters: t('common.filter.button'),
        reset: t('common.filter.reset'),
        panelAriaLabel: t('stockChart.filters.aria_label'),
        activeAriaLabel: t('common.filter.active_aria_label'),
      }}
      panel={
        <>
          {/* wireframe `Stocuri.tsx:32-40`: the period is a named preset, with the
          two dates shown only when the reader asks for a custom interval. */}
          <div className="simsm-filter-bar__field">
            <MudSelect
              size="medium"
              label={t('stockChart.filters.period')}
              value={period}
              onMudChange={(e: CustomEvent<SelectChangeDetail>) => {
                const next = e.detail.value as PeriodPresetId;
                const range = rangeForPreset(next);
                setCustomAsked(range === null);
                if (range) onChange(range);
              }}
            >
              {PERIOD_PRESETS.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {t(`stockChart.period.${preset.id}`)}
                </option>
              ))}
              <option value="custom">{t('stockChart.period.custom')}</option>
            </MudSelect>
          </div>

          {/* UC91-06 (TC-UC91-06-01): a preset hides its two dates, so the interval it
              stands for is printed beside it, as YYYY-MM-DD, the form the address keeps. */}
          {period !== 'custom' ? (
            <p className="simsm-filter-bar__field-hint" data-testid="period-range">
              {t('common.filter.range_value', { from: state.from, to: state.to })}
            </p>
          ) : null}

          {period === 'custom' ? (
            <>
              <div className="simsm-filter-bar__field">
                <DraftDateInput
                  placeholder={t('common.date.placeholder')}
                  size="md"
                  label={t('stockChart.filters.from')}
                  value={isoToDateInput(state.from)}
                  invalid={!periodOk}
                  onMudChange={(e: CustomEvent<{ value: string }>) =>
                    onChange({ from: dateInputToIso(e.detail.value) })
                  }
                />
              </div>

              <div className="simsm-filter-bar__field">
                <DraftDateInput
                  placeholder={t('common.date.placeholder')}
                  size="md"
                  label={t('stockChart.filters.to')}
                  value={isoToDateInput(state.to)}
                  invalid={!periodOk}
                  errorText={t('stockChart.period.invalid')}
                  onMudChange={(e: CustomEvent<{ value: string }>) =>
                    onChange({ to: dateInputToIso(e.detail.value) })
                  }
                />
              </div>
            </>
          ) : null}

          {levels.length > 1 ? (
            <div className="simsm-filter-bar__field">
              <MudSelect
                size="medium"
                label={t('stockChart.filters.level')}
                value={state.level}
                onMudChange={(e: CustomEvent<SelectChangeDetail>) => {
                  const level = e.detail.value as AggregationLevel;
                  // UC91-14: a lot narrows the unaggregated view only, so it does
                  // not follow the reader into an aggregated level.
                  onChange(level === 'none' ? { level } : { level, lots: [] });
                }}
              >
                {levels.map((level) => (
                  <option key={level} value={level}>
                    {t(`stockChart.level.${level}`)}
                  </option>
                ))}
              </MudSelect>
            </div>
          ) : null}

          {canFilterRegion ? (
            <MultiScopeField
              label={t('stockChart.filters.region')}
              placeholder={t('stockChart.filters.all_regions')}
              options={options.regions}
              selected={state.regionIds}
              onChange={(regionIds) => onChange({ regionIds, localityIds: [], siteIds: [] })}
            />
          ) : null}

          {/* UC91-11 (TC-UC91-11-01): the locality filter belongs to the locality level
              alone. At «Fără agregare» the reader narrows by Registrar, location and lot. */}
          {canFilterLocality && state.level === 'locality' ? (
            <MultiScopeField
              label={t('stockChart.filters.locality')}
              placeholder={t('stockChart.filters.all_localities')}
              options={options.localities}
              selected={state.localityIds}
              onChange={(localityIds) => onChange({ localityIds, siteIds: [] })}
            />
          ) : null}

          {canFilterRegistrar ? (
            <MultiScopeField
              label={t('stockChart.filters.registrar')}
              placeholder={t('stockChart.filters.all_registrars')}
              options={options.registrars}
              selected={state.registrarIdnos}
              onChange={(registrarIdnos) => onChange({ registrarIdnos, siteIds: [] })}
            />
          ) : null}

          {unaggregated ? (
            <MultiScopeField
              label={t('stockChart.filters.site')}
              placeholder={t('stockChart.filters.all_sites')}
              options={options.sites}
              selected={state.siteIds}
              onChange={(siteIds) => onChange({ siteIds })}
            />
          ) : null}

          {unaggregated ? (
            <LotFilterField
              selected={state.lots}
              registrarIds={state.registrarIdnos}
              siteIds={state.siteIds}
              onChange={(lots) => onChange({ lots })}
            />
          ) : null}
        </>
      }
    />
  );
}
